import { Text, View } from '@react-pdf/renderer';

import { CommentarySection } from '../components/CommentarySection.tsx';
import { CompTypeBadge } from '../components/CompTypeBadge.tsx';
import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles, reconciliationPageStyles } from '../styles.ts';
import type { ReconciliationPageProps } from '../types.ts';

export const ReconciliationPage = ({
  subjectAddress,
  reconciliationCommentary,
  highestAndBestUseCommentary,
  feasibilityCommentary,
  aiv,
  arv,
  alternativeComparablesCommentary,
  totalCostToCure,
  opinionOfMarketValue,
  totalCostToCureCommentary,
  revisionCommentary,
  isRevision,
  revisionType,
  commentaryV2,
  alerts,
  revisionData,
  opinionOfAfterRepairValue,
  includeTotalCostToCure,
  includeOpinionOfAfterRepairValue,
  includeTotalRenovationBudget,
  totalRenovationBudget,
  estimatedROI,
  hasNegativeROI,
  effectiveDate,
}: ReconciliationPageProps) => {
  const shouldDisplayAlertCommentarySection = alerts
    .filter(({ status }) => status === 'dismissed')
    .some(({ type }) => commentaryV2.alerts?.[type]);

  type PriceValue = { toString: (withSymbol?: boolean) => string };

  const isPriceValue = (value: unknown): value is PriceValue =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PriceValue).toString === 'function';
  const priceToString = (value: unknown, withSymbol = true) =>
    isPriceValue(value) ? value.toString(withSymbol) : 'N/A';

  const daysToString = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) ? `${value} days` : 'N/A';

  const {
    costApproachValue: indicatedValueByCostApproach,
    maxCompMarketAdjustedSalePrice,
    minCompMarketAdjustedSalePrice,
    averageCompMarketAdjustedSalePrice,
    medianCompMarketAdjustedSalePrice,
    averageCompDaysOnMarket,
    medianCompDaysOnMarket,
  } = aiv;

  const {
    costApproachValue: arvIndicatedValueByCostApproach,
    maxCompMarketAdjustedSalePrice: arvMaxCompMarketAdjustedSalePrice,
    minCompMarketAdjustedSalePrice: arvMinCompMarketAdjustedSalePrice,
    averageCompMarketAdjustedSalePrice: arvAverageCompMarketAdjustedSalePrice,
    medianCompMarketAdjustedSalePrice: arvMedianCompMarketAdjustedSalePrice,
    averageCompDaysOnMarket: arvAverageCompDaysOnMarket,
    medianCompDaysOnMarket: arvMedianCompDaysOnMarket,
  } = arv || {};

  const indicatedValueByCostApproachDisplay = priceToString(indicatedValueByCostApproach, true);
  const arvIndicatedValueByCostApproachDisplay = priceToString(
    arvIndicatedValueByCostApproach,
    true,
  );
  const adjustedSalesPriceRangeDisplay = (() => {
    const hasMin = isPriceValue(minCompMarketAdjustedSalePrice);
    const hasMax = isPriceValue(maxCompMarketAdjustedSalePrice);

    if (hasMin && hasMax) {
      return `${minCompMarketAdjustedSalePrice.toString(true)} - ${maxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    if (hasMin) {
      return `${minCompMarketAdjustedSalePrice.toString(true)} - N/A`;
    }
    if (hasMax) {
      return `N/A - ${maxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    return 'N/A';
  })();
  const arvAdjustedSalesPriceRangeDisplay = (() => {
    const hasMin = isPriceValue(arvMinCompMarketAdjustedSalePrice);
    const hasMax = isPriceValue(arvMaxCompMarketAdjustedSalePrice);

    if (hasMin && hasMax) {
      return `${arvMinCompMarketAdjustedSalePrice.toString(true)} - ${arvMaxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    if (hasMin) {
      return `${arvMinCompMarketAdjustedSalePrice.toString(true)} - N/A`;
    }
    if (hasMax) {
      return `N/A - ${arvMaxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    return 'N/A';
  })();
  const averageComparableAdjustedSalePriceDisplay = priceToString(
    averageCompMarketAdjustedSalePrice,
  );
  const medianComparableAdjustedSalePriceDisplay = priceToString(medianCompMarketAdjustedSalePrice);
  const averageComparableDaysOnMarketDisplay = daysToString(averageCompDaysOnMarket);
  const medianComparableDaysOnMarketDisplay = daysToString(medianCompDaysOnMarket);
  const arvAverageComparableAdjustedSalePriceDisplay = priceToString(
    arvAverageCompMarketAdjustedSalePrice,
  );
  const arvMedianComparableAdjustedSalePriceDisplay = priceToString(
    arvMedianCompMarketAdjustedSalePrice,
  );
  const arvAverageComparableDaysOnMarketDisplay = daysToString(arvAverageCompDaysOnMarket);
  const arvMedianComparableDaysOnMarketDisplay = daysToString(arvMedianCompDaysOnMarket);

  return (
    <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
      {includeTotalCostToCure && (
        <CommentarySection
          title="Total Cost to Cure Commentary"
          commentary={totalCostToCureCommentary}
        />
      )}
      {isRevision && (
        <>
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>{revisionType} Commentary</Text>
            <Text style={fontStyles.body}>{revisionCommentary?.replace('\n', '\n\n')}</Text>
          </View>
          {alternativeComparablesCommentary?.map((commentary, index) => {
            const comparableKey =
              revisionData?.comparables?.[index]?.address ?? `alternative-comparable-${index}`;
            return (
              <View key={comparableKey} style={reconciliationPageStyles.section}>
                <Text style={fontStyles.bodyBold}>
                  Alternative Comparable {index + 1}:{' '}
                  {revisionData?.comparables?.[index]?.address || 'N/A'}
                </Text>
                <Text style={fontStyles.body}>{commentary?.replace('\n', '\n\n')}</Text>
              </View>
            );
          })}
        </>
      )}

      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Opinion of Value</Text>
        <View
          style={{
            ...gridStyles.cardContainer,
            paddingHorizontal: 16,
            paddingVertical: 12,
            gap: 12,
          }}
        >
          {includeTotalCostToCure && (
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.h4}>Total Cost to Cure</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{totalCostToCure}</Text>
              </View>
            </View>
          )}
          {includeTotalRenovationBudget && (
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.h4}>Renovation Budget</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{totalRenovationBudget}</Text>
              </View>
            </View>
          )}
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>
                {includeOpinionOfAfterRepairValue
                  ? 'Opinion of As-Is Value'
                  : 'Opinion of Market Value'}
              </Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{opinionOfMarketValue}</Text>
            </View>
          </View>
          {includeOpinionOfAfterRepairValue && (
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.h4}>Opinion of After-Repair Value</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{opinionOfAfterRepairValue}</Text>
              </View>
            </View>
          )}
          {includeOpinionOfAfterRepairValue && (
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.h4}>Estimated Return on Investment</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text
                  style={[
                    fontStyles.body,
                    hasNegativeROI ? fontStyles.negative : fontStyles.positive,
                  ]}
                >
                  {estimatedROI}
                </Text>
              </View>
            </View>
          )}
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Effective Date</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{effectiveDate}</Text>
            </View>
          </View>
        </View>
        <View style={{ marginTop: 8 }}>
          <Text style={fontStyles.small}>
            The opinion of market value stated in this report is based on the defined scope of work,
            statement of assumptions and limiting conditions, and appraiser&apos;s certification.
          </Text>
        </View>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Values as Indicated</Text>
        {includeOpinionOfAfterRepairValue ? (
          <View
            style={{
              ...gridStyles.cardContainerMuted,
              paddingHorizontal: 16,
              paddingVertical: 12,
              gap: 12,
            }}
          >
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="aiv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Cost Approach Indicated Value</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <Text style={fontStyles.body}>{indicatedValueByCostApproachDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="aiv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Sales Comparison Range (Adjusted)</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <Text style={fontStyles.body}>{adjustedSalesPriceRangeDisplay}</Text>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Avg:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {averageComparableAdjustedSalePriceDisplay}
                  </Text>
                </View>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Med:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {medianComparableAdjustedSalePriceDisplay}
                  </Text>
                </View>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="aiv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Comparable Days on Market</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Avg:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {averageComparableDaysOnMarketDisplay}
                  </Text>
                </View>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Med:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {medianComparableDaysOnMarketDisplay}
                  </Text>
                </View>
              </View>
            </View>
            <View style={gridStyles.gridRow}></View>
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="arv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Cost Approach Indicated Value</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <Text style={fontStyles.body}>{arvIndicatedValueByCostApproachDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="arv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Sales Comparison Range (Adjusted)</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <Text style={fontStyles.body}>{arvAdjustedSalesPriceRangeDisplay}</Text>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Avg:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {arvAverageComparableAdjustedSalePriceDisplay}
                  </Text>
                </View>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Med:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {arvMedianComparableAdjustedSalePriceDisplay}
                  </Text>
                </View>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={[gridStyles.row, { width: 220 }]}>
                <CompTypeBadge compType="arv" compact fullText />
                <Text style={fontStyles.bodyRegularLight}>Comparable Days on Market</Text>
              </View>
              <View style={[gridStyles.row, { marginLeft: 8, gap: 16 }]}>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Avg:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {arvAverageComparableDaysOnMarketDisplay}
                  </Text>
                </View>
                <View style={gridStyles.row}>
                  <Text
                    style={[
                      fontStyles.bodyRegularLight,
                      fontStyles.regularSmall,
                      { marginRight: -4 },
                    ]}
                  >
                    Med:
                  </Text>
                  <Text style={[fontStyles.body, fontStyles.regularSmall]}>
                    {arvMedianComparableDaysOnMarketDisplay}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        ) : (
          <View
            style={{
              ...gridStyles.cardContainerMuted,
              paddingHorizontal: 16,
              paddingVertical: 12,
              gap: 12,
            }}
          >
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>Indicated Value by Cost Approach</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{indicatedValueByCostApproachDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>Adjusted Sales Price Range</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{adjustedSalesPriceRangeDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>
                  Average Comparable Adjusted Sale Price
                </Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{averageComparableAdjustedSalePriceDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>
                  Median Comparable Adjusted Sale Price
                </Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{medianComparableAdjustedSalePriceDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>Average Comparable Days on Market</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{averageComparableDaysOnMarketDisplay}</Text>
              </View>
            </View>
            <View style={gridStyles.row}>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.bodyRegularLight}>Median Comparable Days on Market</Text>
              </View>
              <View style={gridStyles.gridCol}>
                <Text style={fontStyles.body}>{medianComparableDaysOnMarketDisplay}</Text>
              </View>
            </View>
          </View>
        )}
      </View>
      {feasibilityCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Feasibility Commentary</Text>
          <Text style={fontStyles.body}>{feasibilityCommentary?.replace('\n', '\n\n')}</Text>
        </View>
      )}
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Reconciliation Commentary</Text>
        <Text style={fontStyles.body}>{reconciliationCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Highest & Best Use Commentary</Text>
        <Text style={fontStyles.body}>{highestAndBestUseCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      {shouldDisplayAlertCommentarySection && (
        <View style={{ ...gridStyles.gridSection, marginRight: 20 }}>
          <Text style={fontStyles.h5}>Notes</Text>
          <List
            type="number"
            items={alerts
              .filter(({ status }) => status === 'dismissed')
              .map(({ type, message }) => (
                <Text key={type} style={fontStyles.body}>
                  <Text style={fontStyles.bodyBold}>{message}&nbsp;</Text>
                  {commentaryV2.alerts?.[type]}
                </Text>
              ))}
          />
        </View>
      )}
    </Tkv2PDFPage>
  );
};
